import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Auto-logout on inactivity.
 * - Listens to: mousemove, click, keydown, scroll, touchstart
 * - If no activity for timeoutMs → clears session + redirectPath
 */
export default function useIdleLogout({
  timeoutMs = 60 * 60 * 1000,   // 🔥 5 minutes
  redirectPath = "/",          
} = {}) {
  const navigate = useNavigate();
  const logoutRef = useRef(false);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    if (logoutRef.current) return;
    logoutRef.current = true;

    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      localStorage.removeItem("user_id");
      localStorage.removeItem("admin_id");
      localStorage.removeItem("is_super");
    } catch {}

    console.log("[IdleLogout] Redirecting to", redirectPath);
    navigate(redirectPath, { replace: true });
  }, [navigate, redirectPath]);

  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      const now = Date.now();
      if (now - lastActivityRef.current >= timeoutMs) {
        console.log("[IdleLogout] Idle threshold reached → logging out");
        handleLogout();
      }
    }, timeoutMs);
  }, [handleLogout, timeoutMs]);

  useEffect(() => {
    // Start timer when component mounts
    resetIdleTimer();

    const events = ["mousemove", "click", "keydown", "scroll", "touchstart"];
    const onActivity = () => resetIdleTimer();

    events.forEach((ev) => window.addEventListener(ev, onActivity));

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer]);
}
